import { machineFailureResultVariant } from './machine-failure-result-row';
import { memberResultVariant } from './member-result-row';
import { offerResultVariant } from './offer-result-row';
import { policyResultVariant } from './policy-result-row';
import { staffUserResultVariant } from './staff-user-result-row';

/**
 * The apply machine fans over one branch per entity type and reports them through ONE merged
 * manifest, so a report's rows are heterogeneous. Each variant owns everything that differs: its
 * columns, how to key a row, how to recognise its own rows, and the row component.
 *
 * To add an entity (P7 staff users): write a `<entity>-result-row.jsx` exporting a
 * variant and add it here. Nothing else changes — `apply-results-table.jsx` and the run detail page
 * are driven entirely by this list.
 *
 * This list is both the match order and the render order. A row goes to the first variant that
 * claims it, so variants MUST keep their `matches` mutually exclusive — then the order is free and
 * can be chosen for display. Policies read first because they are the primary entity.
 */
export const RESULT_VARIANTS = [
  policyResultVariant,
  offerResultVariant,
  memberResultVariant,
  staffUserResultVariant,
  machineFailureResultVariant
];

/**
 * Policies are last-resort on purpose. A row shape no variant recognises must still be visible —
 * dropping it would silently under-report an apply — and the policy columns are what such a row
 * rendered as before there was more than one entity.
 */
const FALLBACK_VARIANT = policyResultVariant;

export const findResultVariant = (details) =>
  RESULT_VARIANTS.find((variant) => variant.matches(details)) ?? FALLBACK_VARIANT;

/** Bucket a report's rows by variant key, preserving order within each bucket. */
export const splitRowsByVariant = (rows = []) => {
  const buckets = Object.fromEntries(RESULT_VARIANTS.map((variant) => [variant.key, []]));

  for (const row of rows) {
    buckets[findResultVariant(row.details ?? row).key].push(row);
  }

  return buckets;
};
